import {
  ClientEmployeeFragment,
  useClientEmployeesQuery,
} from "@graphql/client-employees/clientEmployees.g";
import {
  type ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";
import { ConfigurableTableSystem } from "@utils/table/TableConfigurationSystem";

import {
  ClientEmployeesTableConfig,
  renderName,
  renderEmail,
  renderAppsUsed,
  renderLastActivity,
} from "./ClientEmployeesTableConfig";
import { ClientEmployeeTableColumns } from "./UI/ClientEmployeesTableColumns";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;

type ClientEmployeeWithResourceGroups = ClientEmployeeFragment & {
  resourceGroupsCount: number;
};

const columnHelper = createColumnHelper<ClientEmployeeWithResourceGroups>();

type Props = {
  clientId: string;
  initialPageIndex?: number;
  nameSearch?: string;
  emailSearch?: string;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
  pageSize?: number;
};

export const ClientEmployeesTable = ({
  clientId,
  initialPageIndex = 0,
  nameSearch,
  emailSearch,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
  pageSize = PAGE_SIZE,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { columnSettings, columnOrder } = useTableColumnSettingsProvider();

  const debouncedNameSearch = useDebounce(nameSearch, SEARCH_DEBOUNCE_TIME);
  const debouncedEmailSearch = useDebounce(emailSearch, SEARCH_DEBOUNCE_TIME);
  const clientEmployeesQuery = useClientEmployeesQuery({
    clientId,
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    // Separate search parameters for name and email
    name_Icontains: debouncedNameSearch || undefined,
    email_Icontains: debouncedEmailSearch || undefined,
    orderBy: ConfigurableTableSystem.generateSortParameter(
      ClientEmployeesTableConfig.columns,
      sorting,
    ),
  });
  const navigate = useNavigate();
  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isClientEmployeesLoading = clientEmployeesQuery.isLoading;
  const isClientEmployeesFetching = clientEmployeesQuery.isFetching;

  const totalCount =
    clientEmployeesQuery.data?.clientEmployees?.totalCount ?? undefined;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: pageSize });
  }, [debouncedNameSearch, debouncedEmailSearch, pageSize]);

  const data = useMemo(() => {
    const employees = clientEmployeesQuery.data?.clientEmployees?.edges
      .map((edge) => edge?.node)
      .filter((clientEmployee) => !!clientEmployee) ?? [];

    // Use the resourceGroupCount directly from the fragment
    return employees.map((employee) => {
      return {
        ...employee,
        resourceGroupsCount: employee.resourceGroupCount || 0,
      };
    });
  }, [clientEmployeesQuery.data]);

  const handleDetailsClick = useCallback(
    (clientEmployeeId: string) => {
      navigate(
        routes.portal.clients.client.clientEmployees.clientEmployee.dashboard.$buildPath(
          {
            params: { clientId, clientEmployeeId },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const handleEditClick = useCallback(
    (clientEmployeeId: string) => {
      navigate(
        routes.portal.clients.client.clientEmployees.clientEmployee.record.$buildPath(
          {
            params: { clientId, clientEmployeeId },
          },
        ),
      );
    },
    [clientId, navigate],
  );

  const configuredColumns = useMemo(() => {
    // Create column configuration with custom renderers
    const configWithRenderers = {
      ...ClientEmployeesTableConfig,
      columns: Object.fromEntries(
        Object.entries(ClientEmployeesTableConfig.columns).map(
          ([key, config]) => [
            key,
            {
              ...config,
              render: (value: unknown, row: ClientEmployeeWithResourceGroups) => {
                const columnKey = key as ClientEmployeeTableColumns;

                // Map to the appropriate render function
                switch (columnKey) {
                  case ClientEmployeeTableColumns.name:
                    return renderName(value, row, handleDetailsClick);
                  case ClientEmployeeTableColumns.email:
                    return renderEmail(value);
                  case ClientEmployeeTableColumns.appsUsed:
                    return renderAppsUsed(value);
                  case ClientEmployeeTableColumns.lastActivity:
                    return renderLastActivity(value);
                  default:
                    return String(value);
                }
              },
            },
          ],
        ),
      ) as typeof ClientEmployeesTableConfig.columns,
    };

    return ConfigurableTableSystem.generateColumnsFromConfig(
      data,
      configWithRenderers,
      {
        columnOrder:
          columnOrder.length > 0
            ? (columnOrder as ClientEmployeeTableColumns[])
            : undefined,
        columnVisibility: columnSettings,
      },
    );
  }, [data, columnOrder, columnSettings, handleDetailsClick]);

  const allColumnsWithActions = useMemo(() => {
    const columns: ColumnDef<ClientEmployeeWithResourceGroups, unknown>[] = [
      ...configuredColumns,
    ];

    // Always add the actions column at the end
    columns.push(
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          sticky: true,
          actions: true,
        },
        cell: (props) => {
          return (
            <Box gap={2}>
              <Dropdown
                trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
              >
                <Dropdown.Option
                  label="Details"
                  icon="faFileLines"
                  onSelect={() => handleDetailsClick(props.row.original.id)}
                />
                <Dropdown.Option
                  label="Edit"
                  icon="faPencil"
                  onSelect={() => handleEditClick(props.row.original.id)}
                />
              </Dropdown>
            </Box>
          );
        },
      }),
    );

    return columns;
  }, [configuredColumns, handleDetailsClick, handleEditClick]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table<ClientEmployeeWithResourceGroups, string>
      columns={allColumnsWithActions as ColumnDef<ClientEmployeeWithResourceGroups, string>[]}
      data={data}
      rowCount={totalCount}
      onPaginationChange={handlePaginationChange}
      pagination={pagination}
      isLoading={isClientEmployeesLoading}
      isFetching={isClientEmployeesFetching}
      onPageSizeChange={handlePageSizeChange}
      sorting={sorting}
      onSortingChange={setSorting}
      showFooter={showFooter}
      showHeader={showHeader}
      showPageSizeDropdown={showPageSizeDropdown}
      columnVisibility={columnSettings}
    />
  );
};
